package com.downdogapp.client.controllers.playback

import com.downdogapp.*
import com.downdogapp.client.*
import com.downdogapp.client.Orientation.LANDSCAPE
import com.downdogapp.client.Orientation.PORTRAIT
import com.downdogapp.client.api.*
import com.downdogapp.client.api.AppType.*
import com.downdogapp.client.api.FeedbackType.PRACTICE_FEEDBACK
import com.downdogapp.client.controllers.*
import com.downdogapp.client.controllers.start.StartViewController
import com.downdogapp.client.resources.Images
import com.downdogapp.client.singleton.*
import com.downdogapp.client.singleton.Platform.ANDROID
import com.downdogapp.client.singleton.Platform.IOS
import com.downdogapp.client.singleton.Platform.WEB
import com.downdogapp.client.singleton.PlayerState.PAUSED
import com.downdogapp.client.singleton.PlayerState.PLAYING
import com.downdogapp.client.views.playback.PlaybackView
import kotlin.math.abs
import kotlin.math.pow
import kotlin.random.Random

class PlaybackViewController(val sequence: Sequence,
                             playlist: Playlist?,
                             val player: MediaPlayer,
                             private var playbackUrl: String?,
                             sequenceStartTime: Duration = Duration.ZERO,
                             var videoOffsetTime: Duration = Duration.ZERO) :
  ViewController(if (sequence.videoWidthToHeight < 1) PORTRAIT else LANDSCAPE) {

  var currentSequenceTime = sequenceStartTime; private set
  var isPlaying = false; private set
  var currentPose = getCurrentPose(sequenceStartTime); private set
  val currentPoseLiked get() = currentPose?.poseId?.let { SequenceSettings.getPoseLiked(it) }
  val isOffline = playbackUrl == null

  private var currentUrlRequestId: Int? = null
  private var playAfterUrlResponse: Boolean = false
  private var updatingPlaybackUrl = false
  private var postPractice = false
  private var pausedBeforeSeek = true
  private var exited = false
  private var poseConfirmCount = 0

  private val currentPlaylistId get() = songsController.playlist?.playlistId

  val showSkipButton get() = songsController.playlist?.disableSkip == false && !isOffline
  val showDownloadButton get() = MANIFEST.displayOfflinePracticesPage && !isOffline
  val showFeedbackButton get() = MANIFEST.displayFeedbackButtonOnPlayback
  val showPoseImages get() = !isOffline
  val isAirplayVideoActive get() = player.isAirplayVideoActive
  val sequenceLength = sequence.snapTimes.last()
  val sequenceId = sequence.sequenceId

  var isScrollingImagesManually = false
    set(value) {
      field = value
      scrollPoseImageTimeline(currentSequenceTime) // Sets opacity
    }

  override val view = PlaybackView(this)

  private val progressController = ProgressController(
    sequence = sequence,
    getCurrentSequenceTime = { currentSequenceTime },
    isOffline = isOffline
  )

  val songsController = SongsController(
    initialPlaylist = playlist,
    sequenceId = sequence.sequenceId,
    playbackViewController = this
  )

  val showPosesOnTimeline
    get() =
      PlaybackUtil.showPosesOnTimeline
      && !isOffline
      && supportsPosesOnTimeline
      && !Cast.isCasting

  init {
    Cast.eventHandler = object : CastEventHandler {

      override fun onCastStateChanged(old: CastState, new: CastState) {
        view.onCastStateChanged(old, new)
        view.updatePoseImageTimelineVisibility()
        if (Cast.isStarting(old, new)) {
          player.pause()
          view.hideVideo()
          updatePlaybackUrl()
        } else if (Cast.isEnding(old, new)) {
          view.showVideo()
          updatePlaybackUrl()
        }
        App.updateFullscreen()
      }

      override fun onPlayerStateChanged() {
        when (Cast.playerState) {
          PAUSED -> {
            pause()
          }

          PLAYING -> {
            if (updatingPlaybackUrl) {
              Cast.pause()
            } else {
              play()
            }
          }

          else -> {}
        }
      }
    }

    // turning on duckOthers disables the now playing control center on iOS, so we only do it
    // when there's no playlist (it's only there at all so they can play Spotify seperately)
    App.activatePlaybackSession(
      duckOthers = playlist == null || PlaybackUtil.audioBalance == 1.0
    )
    App.setupPlaybackControls(
      onPlay = ::play,
      onPause = ::pause,
      onChangePlaybackPosition = { onTimeChanged(videoOffsetTime + seconds(it), isSeek = true) },
      onSkipForward = ::stepForward,
      onSkipBackward = ::stepBackward
    )
    view.onViewLoaded()
    songsController.updateCurrentSongIfNeeded()
    view.addVideoPlayer(player)
    timerStep()
    view.onTimeChanged(currentSequenceTime)
    view.refreshView()
  }

  override fun onPopped() {
    view.onPopped()
  }

  override fun viewDidLayoutSubviews() {
    view.updateVideoFrame()
  }

  fun updatePlaybackUrl() {
    playbackUrl?.let {
      updatingPlaybackUrl = true
      view.showSpinner()

      val requestId = Random.nextInt()
      Network.post(CancelStitchRequest(it))
      playAfterUrlResponse = isPlaying || playAfterUrlResponse
      pause()

      currentUrlRequestId = requestId
      Network.post(
        PlaybackUtil.playbackUrlRequest(
          sequence = sequence,
          playlistId = currentPlaylistId,
          videoOffsetTime = currentSequenceTime,
          airplay = player.isAirplayVideoActive
        )
      ) { json ->
        if (requestId != currentUrlRequestId) { // Another url request is inflight, aborting the out-of-date one
          json?.let {
            Network.post(CancelStitchRequest(PlaybackUrlResponse.fromJson(json).url))
          }
          return@post
        } else {
          currentUrlRequestId = null
        }

        if (json == null) {
          updatingPlaybackUrl = false
          logEvent("failed_fetch_url")
          App.alert(
            message = Strings.UNABLE_TO_LOAD_PLAYBACK_URL,
            positiveButton = AlertAction(Strings.TRY_AGAIN) {
              logEvent("failed_fetch_url_retry")
              updatePlaybackUrl()
            },
            negativeButton = AlertAction(Strings.EXIT) {
              logEvent("failed_fetch_url_exit")
              exitToStartScreen()
            }
          )
        } else {
          val response = PlaybackUrlResponse.fromJson(json)

          playbackUrl = response.url
          videoOffsetTime = response.videoOffsetTime
          player.setFile(
            file = CommonUri(playbackUrl!!),
            seekTime = currentSequenceTime - videoOffsetTime
          )

          fun onReady() {
            updatingPlaybackUrl = false
            if (playAfterUrlResponse) {
              play()
              playAfterUrlResponse = false
            }

            view.refreshView()
            view.hideSpinner()
            logEvent("url_fetched", mapOf("sequenceId" to sequence.sequenceId))
          }

          if (Cast.isCasting) {
            Cast.load(
              LoadParams(
                title = MANIFEST.castContentTitle,
                videoUrl = playbackUrl!!,
                playbackStartTime = (currentSequenceTime - videoOffsetTime).coerceIn(Duration.ZERO..player.duration)
              )
            )
            onReady()
          } else {
            fun checkReady() {
              if (player.canPlay) {
                onReady()
              } else {
                App.runAfter(millis(50)) {
                  checkReady()
                }
              }
            }

            checkReady()
          }
        }
      }
    }
  }

  fun togglePlaying() {
    if (isPlaying) {
      pause()
    } else {
      play()
    }
  }

  fun pause(): Boolean {
    if (!isPlaying) {
      return false
    }

    logEvent("pause")
    isPlaying = false
    if (Cast.isCasting) {
      Cast.pause()
    } else {
      player.pause()
    }
    view.pause()
    progressController.recordProgress()
    songsController.updateCurrentSongIfNeeded()
    App.preventSleep(false)
    App.setPlaybackState(false, currentSequenceTime, sequenceLength)
    return true
  }

  fun play(): Boolean {
    if (isPlaying) {
      return false
    }
    logEvent("play")

    pausedBeforeSeek = false // Reset any time play is called
    if (currentSequenceTime >= sequenceLength) {
      onSequenceEndReached()
    }

    isPlaying = true
    if (Cast.isCasting) {
      Cast.play()
    } else {
      player.play()
    }
    view.play()
    songsController.recordSongPlayIfNeeded()
    App.preventSleep(true)
    App.setPlaybackState(true, currentSequenceTime, sequenceLength)
    return true
  }

  private fun onTimeChanged(time: Duration, isSeek: Boolean) {
    if (isSeek) {
      currentUrlRequestId = null // Clear out any pending url requests - the time is changing
    }

    val oldPose = currentPose
    currentPose = getCurrentPose(time)
    if (oldPose != currentPose) {
      view.refreshView()
    }
    val oldTime = currentSequenceTime
    if (isSeek) {
      progressController.recordProgress(newSequenceTime = time)
    }
    updateCurrentTime(time)
    if (!isSeek) {
      progressController.recordProgressIfNeeded()
    }

    if (time < oldTime - seconds(0.5)
        && currentPlaylistId != null
        && !isOffline
        && (isSeek || Cast.isCasting)) {
      view.showSpinner()
      updatingPlaybackUrl = true
      songsController.fetchNewPlaylistFromRewind(rewindAmount = oldTime - time) { success ->
        if (success) {
          updatePlaybackUrl()
        } else {
          updatingPlaybackUrl = false
          view.hideSpinner()
        }
      }
    } else {
      songsController.updateCurrentSongIfNeeded()
      if (isSeek) {
        seekVideoToCurrentTime()
      }
    }
  }

  private fun getCurrentPose(time: Duration) =
    if (sequence.poses.isNotEmpty()) {
      sequence.poses[binarySearch(sequence.poseTimes, time)]
    } else {
      null
    }

  private fun updateCurrentTime(time: Duration) {
    currentSequenceTime = time
    view.onTimeChanged(currentSequenceTime)
    scrollPoseImageTimeline(currentSequenceTime)
    if (isPlaying && time >= sequenceLength) {
      onSequenceEndReached()
    }
  }

  private fun timerStep() {
    if (!exited) {
      if (!updatingPlaybackUrl && Cast.castState != CastState.CONNECTING) {
        if (MANIFEST.autoUpdatePlaybackUrl
            && isPlaying
            && !postPractice
            && player.duration > seconds(1) // android returns small negative duration at start
            && player.currentTime > player.duration - seconds(0.1)) {
          // This can occur if stitching is interrupted e.g. because of having to deploy
          logEvent("reached_end_of_video")
          updatePlaybackUrl()
        } else {
          if (Cast.isCasting) {
            // playerTime unreliable if not playing
            if (Cast.playerState == PLAYING) {
              val newTime = Cast.playerTime?.let { seconds(it) + videoOffsetTime }
              if (newTime != null && newTime != currentSequenceTime) {
                onTimeChanged(newTime, isSeek = abs(newTime.seconds - currentSequenceTime.seconds) > 5)
              }
            }
          } else {
            val newTime = player.currentTime + videoOffsetTime
            if (newTime != currentSequenceTime) {
              onTimeChanged(newTime, isSeek = false)
            }
            if (isPlaying && !player.isPlaying) {
              player.play()
            } else if (!isPlaying && player.isPlaying) {
              player.pause()
            }
          }
        }
      }
      App.runAfter(seconds(0.2), onlyIfActive = false) {
        timerStep()
      }
    }
  }

  fun stepForward() =
    stepForwardOrBack(true)

  fun stepBackward() =
    stepForwardOrBack(false)

  private fun stepForwardOrBack(forward: Boolean) {
    var snapIndex = binarySearch(sequence.snapTimes, currentSequenceTime)
    if (forward) {
      if (snapIndex < sequence.snapTimes.size - 1) {
        snapIndex++
      }
    } else if (snapIndex > 0
               && currentSequenceTime < sequence.snapTimes[snapIndex] + seconds(2)) {
      snapIndex--
    }
    onTimeChanged(sequence.snapTimes[snapIndex], isSeek = true)
  }

  private fun onSequenceEndReached() {
    if (!postPractice) {
      postPractice = true
      progressController.logPractice()
      App.preventSleep(false)
      App.unwindToViewController(PlaybackViewController::class)
      App.pushViewController(
        PostPracticeViewController(
          playbackViewController = this,
          requestReview = progressController.getTotalTimePlayed() >= maxOf(minutes(4), sequence.snapTimes.last() * 0.95)
        )
      )
    }
  }

  fun sendFeedbackClicked() {
    pause()
    App.pushViewController(
      MessageViewController(
        message = MANIFEST.practiceFeedbackMessage!!,
        feedbackType = PRACTICE_FEEDBACK,
        sequenceId = sequence.sequenceId,
        sequenceTime = currentSequenceTime
      )
    )
  }

  fun exitToStartScreen() {
    onExit()
    when (App.platform) {
      WEB -> App.goHomeOrOpenApp()
      IOS, ANDROID -> {
        logEvent("practice_finished")
        HistoryUtil.fetchHistory()
        SequenceSettings.fetchSequences() // get new sequences in case they start a new practice
        App.unwindToViewController(StartViewController::class)
        StartViewController.goToPage(StartViewController.visiblePages.first())
      }
    }
    App.deactivatePlaybackSession()
  }

  override fun onClose() {
    onExit()
  }

  private fun onExit() {
    // Fade out music
    val initialVolume = player.volume
    val numIncrements = 10 // 1 second total
    for (i in 1..numIncrements) {
      App.runAfter(seconds(0.1) * i) {
        val proportion = (numIncrements - i).toDouble() / numIncrements
        player.volume = initialVolume * proportion.pow(2)
        if (i == numIncrements) { // Last iteration has finished
          pause()
          player.volume = initialVolume // this is important on Android where the player is in a service and reused
          player.releasePlayer()
        }
      }
    }
    view.removeVideoPlayer(true)
    progressController.recordProgress()
    progressController.logPractice()
    Cast.stop()
    playbackUrl?.let {
      Network.post(CancelStitchRequest(it))
    }
    exited = true
  }

  override fun onBackgrounded() {
    if (App.platform == IOS) {
      view.removeVideoPlayer()
    }
  }

  override fun onForegrounded() {
    if (App.platform == IOS) {
      view.addVideoPlayer(player)
    }
    seekVideoToCurrentTime()
  }

  override fun onBackClicked() {
    App.alert(
      title = Strings.EXIT,
      message = Strings.EXIT_PRACTICE_MESSAGE,
      negativeButton = AlertAction(Strings.CANCEL) {
        App.updateFullscreen()
      },
      positiveButton = AlertAction(Strings.OK) {
        logEvent("exit_practice")
        exitToStartScreen()
      }
    )
  }

  override fun onInterrupt() {
    pause()
  }

  private fun seekVideoToCurrentTime() {
    val videoTime = currentSequenceTime - videoOffsetTime
    if (videoTime !in seconds(-0.2)..(player.duration + seconds(0.2))) {
      if (!updatingPlaybackUrl) {
        updatePlaybackUrl()
      }
    } else {
      val seekTime = videoTime.coerceIn(Duration.ZERO..player.duration)
      Cast.playerTime = seekTime.seconds
      player.seekToTime(seekTime)
    }
  }

  fun onSkipSongClicked() {
    view.showSpinner()
    songsController.skipSong {
      if (it) {
        updatePlaybackUrl()
      } else {
        view.hideSpinner()
      }
    }
  }

  fun onLikePoseClicked() {
    currentPose.notNull("Required to have current pose to like pose").let {
      if (currentPoseLiked != true) {
        view.showPoseConfirmation(Strings.POSE_LIKED_MESSAGE(it.name!!), Images.THUMBS_UP_ICON)
        delayedHidePoseConfirmation()
        updatePoseLiked(it.poseId, true)
      } else {
        view.hidePoseConfirmation()
        updatePoseLiked(it.poseId, null)
      }
    }
  }

  fun onDislikePoseClicked() {
    currentPose.notNull("Required to have current pose to dislike pose").let {
      if (currentPoseLiked != false) {
        view.showPoseConfirmation(Strings.POSE_DISLIKED_MESSAGE(it.name!!), Images.THUMBS_DOWN_ICON)
        delayedHidePoseConfirmation()
        updatePoseLiked(it.poseId, false)
      } else {
        view.hidePoseConfirmation()
        updatePoseLiked(it.poseId, null)
      }
    }
  }

  // Hide the pose confirmation after a few seconds, unless we've shown a different confirmation since
  private fun delayedHidePoseConfirmation() {
    val currentCount = ++poseConfirmCount
    App.runAfter(seconds(3)) {
      if (poseConfirmCount == currentCount) {
        view.hidePoseConfirmation()
      }
    }
  }

  private fun updatePoseLiked(poseId: Int, liked: Boolean?) {
    SequenceSettings.setPoseLiked(poseId, liked)
    view.refreshView()
  }

  fun onTimelinePanStarted() {
    pausedBeforeSeek = !isPlaying
    pause()
  }

  fun onTimelinePanEnded(time: Duration) {
    logEvent(
      "timeline_pan",
      mapOf(
        "sequenceId" to sequence.sequenceId,
        "sequenceTime" to time.toString()
      )
    )
    onTimeChanged(time, isSeek = true)
    if (!pausedBeforeSeek) {
      play()
    }
  }

  fun scrollPoseImageTimeline(displayTime: Duration) {
    if (showPosesOnTimeline) {
      val focusedImageIndex = binarySearch(sequence.poseTimes, displayTime + seconds(0.2))
      view.scrollPoseImageTimeline(focusedImageIndex, displayTime)
    }
  }

  fun setAudioBalance(value: Double, finished: Boolean) {
    PlaybackUtil.audioBalance = value
    if (finished) {
      updatePlaybackUrl()
    }
  }

  fun onGearClicked() {
    logEvent("opened_sequence_settings")
    pause()
    App.pushViewController(
      PlaybackSettingsViewController(this)
    )
  }

  fun onSendFeedbackClicked() {
    pause()
    App.pushViewController(
      MessageViewController(
        message = MANIFEST.practiceFeedbackMessage!!,
        feedbackType = PRACTICE_FEEDBACK,
        sequenceId = sequenceId,
        sequenceTime = currentSequenceTime
      )
    )
  }

  fun onPoseClicked(index: Int) {
    onTimeChanged(sequence.poseTimes[index], isSeek = true)
  }

  fun onPoseIconClicked(libraryUrl: String) {
    App.openUrl(libraryUrl)
  }

  fun settingsVideoQualityButtonClicked(onSelected: () -> Unit) {
    if (Network.isOffline()) {
      App.alertNoInternet(message = Strings.CHANGE_VISUAL_TYPE_NO_INTERNET_MESSAGE)
    } else {
      pause()
      val initialSelectedId = PlaybackUtil.videoQualityId
      App.pushViewController(
        SelectorViewController(
          options = MANIFEST.videoQualities.map {
            SelectorOption(id = it.id, label = it.label)
          },
          onOptionSelected = { newId ->
            if (newId != initialSelectedId) {
              PlaybackUtil.videoQualityId = newId
              updatePlaybackUrl()
            }
            onSelected()
          },
          initialSelectedId = initialSelectedId,
          title = Strings.VIDEO_QUALITY
        )
      )
    }
  }

  fun settingsPlaylistButtonClicked(playlistSettingTypeId: Int, onSelected: () -> Unit) {
    if (Network.isOffline()) {
      App.alertNoInternet(message = Strings.PLAYLIST_TYPE_NO_INTERNET_MESSAGE)
    } else {
      pause()
      val initialSelectedId = SequenceSettings.getSelectedId(playlistSettingTypeId)
      App.pushViewController(
        SelectorViewController(
          settingTypeId = playlistSettingTypeId,
          onOptionSelected = { newId ->
            if (newId != initialSelectedId) {
              songsController.onPlaylistSettingChanged { success ->
                if (success) {
                  updatePlaybackUrl()
                }
              }
            }
            onSelected()
          }
        )
      )
    }
  }

  fun downloadPractice() {
    OfflinePractices.validateCanDownloadPractice {
      view.showSpinner()
      OfflinePractices.downloadOfflinePractice(sequenceId) {
        view.hideSpinner()
        view.refreshView()
        App.alert(
          title = Strings.DOWNLOAD_STARTED,
          message = Strings.DOWNLOAD_EXPLANATION
        )
      }
    }
  }

  fun isDownloading() = OfflinePractices.isDownloading(sequenceId)
  fun isDownloaded() = OfflinePractices.isDownloaded(sequenceId)

  /****** Playback Settings ******/

  val supportsPoseNames get() = sequence.supportsPoseNames && !isOffline
  val supportsLikeAndExcludeButtons get() = sequence.supportsLikeAndExcludeButtons
  val supportsClosedCaptions get() = sequence.supportsClosedCaptions && !isOffline
  val supportsAudioBalance get() = sequence.supportsAudioBalance && !isOffline
  val supportsCountdown get() = sequence.supportsCountdown && !isOffline
  val supportsOverlay get() = sequence.supportsOverlay && !isOffline
  val supportsPosesOnTimeline get() = sequence.supportsPoseTimeline && !isOffline
  val supportsMirrorVideo get() = sequence.supportsMirror && !isOffline
  val supportsChangeVideoQuality get() = MANIFEST.videoQualities.size > 1 && !isOffline

  fun supportsChangePlaylistSetting(playlistSettingTypeId: Int) =
    !isOffline && SequenceSettings.allSelectorTypeIds.contains(playlistSettingTypeId)

  fun setShowPoseNames(value: Boolean) {
    if (PlaybackUtil.displayPoseNames != value) {
      PlaybackUtil.displayPoseNames = value
      updatePlaybackUrl()
    }
  }

  fun setShowLikeAndExcludeButtons(value: Boolean) {
    if (PlaybackUtil.showLikeAndExcludeButtons != value) {
      PlaybackUtil.showLikeAndExcludeButtons = value
      view.refreshView()
    }
  }

  fun setShowOverlay(value: Boolean) {
    if (PlaybackUtil.showOverlay != value) {
      PlaybackUtil.showOverlay = value
      updatePlaybackUrl()
    }
  }

  fun setShowCountdown(value: Boolean) {
    if (PlaybackUtil.showCountdown != value) {
      PlaybackUtil.showCountdown = value
      updatePlaybackUrl()
    }
  }

  fun setMirrorVideo(value: Boolean) {
    if (PlaybackUtil.mirrorVideo != value) {
      PlaybackUtil.mirrorVideo = value
      updatePlaybackUrl()
      view.refreshView()
    }
  }

  fun setShowClosedCaptions(value: Boolean) {
    if (PlaybackUtil.showSubtitles != value) {
      PlaybackUtil.showSubtitles = value
      updatePlaybackUrl()
      view.refreshView()
    }
  }

  fun setKeepTimelineVisible(value: Boolean) {
    if (PlaybackUtil.keepTimelineVisible != value) {
      PlaybackUtil.keepTimelineVisible = value
      if (isPlaying) {
        view.updateTimelineVisibility()
      }
    }
  }

  fun setPreferPosesOnTimeline(value: Boolean) {
    if (PlaybackUtil.showPosesOnTimeline != value) {
      PlaybackUtil.showPosesOnTimeline = value
      view.updatePoseImageTimelineVisibility()
    }
  }

  val showPosesOnTimelineLabel
    get() = when (App.appType) {
      ORIGINAL, INTRO, PRENATAL -> Strings.SEE_POSES_ON_TIMELINE
      HIIT, BARRE, SEVEN, PILATES -> Strings.SEE_EXERCISES_ON_TIMELINE
      MEDITATION, RUNNING, BREATHING -> ""
    }

  val showPoseNamesLabel
    get() = when (App.appType) {
      ORIGINAL, INTRO, PRENATAL -> Strings.SHOW_POSE_NAME
      HIIT, BARRE, SEVEN, PILATES -> Strings.SHOW_EXERCISE_NAMES
      MEDITATION, RUNNING, BREATHING -> ""
    }
}
